//third party imports
import { Platform } from '@angular/cdk/platform';
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import {
    AccountInfo,
    AuthenticationResult,
    EventMessage,
    EventType,
    InteractionRequiredAuthError,
} from '@azure/msal-browser';

import {
    MsalBroadcastService,
    MsalService,
} from '@azure/msal-angular';

import { Subject, Subscription, distinctUntilChanged, filter, takeUntil } from 'rxjs';

//local imports
import { Messenger } from '@shared/data/messaging';

import {
    AuthenticationStage,
    LoggingInMessage,
    SetPreviousUrlMessage,
    TenantSwitchSelectionRequestedMessage,
    TenantSyncMessage,
    TokenUpdatedMessage,
    UserLoggedInMessage,
    UserLoggedOutMessage,
} from '@core/messages';

import { 
    AccountContextService,
    AuthAccountService, 
    AuthRequestBuilderService,
    AuthStateService,
    AuthSessionService,
    ProtectedResourceService,
} from '@core/security/services';

import {
    HandleAuthFailureCommand, HandleAuthSuccessCommand,
    LogoutCommand, SwitchTenantCommand 
} from '@core/security/commands';

import {
    AuthRouteHelper,
    AuthEventContextHelper,
    AuthErrorConstants,
    AuthRuntimeConstants,
    securityLog
} from '@core/security/helpers';

import { ApplicationConfiguration } from '@core/configuration';

@Injectable({
    providedIn: 'root'
})
export class AuthService implements OnDestroy {
    private readonly destroying$ = new Subject<void>();

    private authSubscription?: Subscription;
    private forgotPasswordSubscription?: Subscription;

    public isIframe: boolean = false;

    constructor(
        private applicationConfig: ApplicationConfiguration,
        private msalService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private authRequestBuilder: AuthRequestBuilderService,
        private accountContext: AccountContextService,
        private accountService: AuthAccountService,
        private authSession: AuthSessionService,
        private authRouteHelper: AuthRouteHelper,
        private authState: AuthStateService,
        private protectedResourceService: ProtectedResourceService,
        private authEventContext: AuthEventContextHelper,
        private handleAuthSuccess: HandleAuthSuccessCommand,
        private handleAuthFailure: HandleAuthFailureCommand,
        private logoutCommand: LogoutCommand,
        private switchTenantCommand: SwitchTenantCommand,
        private router: Router,
        private messenger: Messenger,
        private platform: Platform,
    ) { }

    public async initialize(): Promise<void> {

        securityLog.debug('AuthService: Initializing');

        await this.authSession.ensureMsalInitialized();

        this.isIframe = this.getIsIframe();

        securityLog.debug(`AuthService: Initializing application {isframe: ${this.isIframe}}`);

        if (this.authRouteHelper.isAuthCallbackPath(this.router.url || '')) {
            securityLog.info('AuthService: /auth callback detected, skipping cached session restore');
        } else {
            const cachedAccount = this.accountService.hydrateTenantFromCachedAccount();

            if (cachedAccount) {
                await this.restoreAuthenticatedSession(cachedAccount);
            }
        }

        this.accountService.enableAccountStorageEvents();

        this.messenger.subscribe(this, TenantSwitchSelectionRequestedMessage, async (message) => {
            const tenantId = (message.tenant?.tenantId() || '').trim();
            const tenantSlug = (message.tenant?.tenantSlug() || '').trim();

            try {
                const result = await this.switchTenantCommand.execute(tenantId || tenantSlug, tenantSlug || tenantId);
                if (result) {
                    this.publishTokenUpdate(result.authenticationResult, result.authority);
                }
            } catch (error) {
                securityLog.error('AuthService: switchTenant invocation failed', error);
            }
        });

        this.messenger.subscribe(this, UserLoggedOutMessage, (message) => {
            this.logoutCommand.execute(message);
        });

        this.authSubscription = this.msalBroadcastService.msalSubject$
            .pipe(
                filter((message: EventMessage) => message.eventType === EventType.LOGIN_SUCCESS || message.eventType === EventType.ACQUIRE_TOKEN_SUCCESS || message.eventType === EventType.SSO_SILENT_SUCCESS),
                distinctUntilChanged((previous, current) =>
                    this.authEventContext.buildAuthEventDeduplicationKey(previous) === this.authEventContext.buildAuthEventDeduplicationKey(current)
                ),
                takeUntil(this.destroying$)
            )
            .subscribe((result: EventMessage) => {
                const commandResult = this.handleAuthSuccess.execute(result);
                if (!commandResult) {
                    securityLog.warn('AuthService: Auth success event could not be processed, no command result returned');
                    return;
                }

                this.publishTokenUpdate(commandResult.authenticationResult, commandResult.authority);

                this.completeAuthenticatedStartup(
                    commandResult.authenticationResult.account,
                    commandResult.tenant,
                    commandResult.stage,
                );
            });

        this.forgotPasswordSubscription =
            this.msalBroadcastService.msalSubject$
                .pipe(
                    filter((message: EventMessage) => message.eventType === EventType.LOGIN_FAILURE || message.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
                    takeUntil(this.destroying$)
                )
                .subscribe((result: EventMessage) => {
                    if (this.handleAuthFailure.execute(result)) {
                        return;
                    }

                    this.restartAuthFlow();
                });

        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.messenger.send(new SetPreviousUrlMessage(this.authState.updateNavigation(event.url)!));
            });
    }

    public hasActiveAccount(): boolean {
        return this.accountService.hasActiveAccount();
    }
    
    private getIsIframe(): boolean {
        if (this.platform.isBrowser) {
            return window !== window.parent && !window.opener;
        }

        return false;
    }
        

    public async acquireApplicationApiAccessToken(preferredAccount?: AccountInfo): Promise<string> {              
        try {
            securityLog.debug('AuthService: acquiring application api access token', {
                preferredHomeAccountId: preferredAccount?.homeAccountId || preferredAccount?.localAccountId || preferredAccount?.username || '',
            });

            const silentResult = await this.authSession.acquireApplicationApiTokenResult(preferredAccount);
            if (!silentResult) {
                securityLog.warn('AuthService: no silent token result returned for application api token acquisition');
                return '';
            }

            this.publishTokenUpdate(silentResult.result, silentResult.authority);

            securityLog.debug('AuthService: application api access token acquired', {
                expiresOn: silentResult.result.expiresOn?.toISOString() || '',
                authority: silentResult.authority || '',
            });

            return silentResult?.result.accessToken || '';
        } catch (error) {
            securityLog.error('AuthService: Token acquisition failed', error);
            const errorCode = (error as { errorCode?: string } | undefined)?.errorCode?.toLowerCase();
            if (error instanceof InteractionRequiredAuthError || (errorCode && AuthErrorConstants.interactionRequiredCodes.has(errorCode))) {
                this.messenger.send(new UserLoggedOutMessage(true));
            }
            return ''
        }
    }
    
    private publishTokenUpdate(authenticationResult: AuthenticationResult, authority?: string): void {
        const accessToken = (authenticationResult.accessToken || '').trim();
        if (!accessToken) {
            return;
        }

        this.messenger.send(new TokenUpdatedMessage(accessToken, authenticationResult.expiresOn || undefined, authority));
        this.scheduleTokenRefresh(authenticationResult.expiresOn || undefined);
    }

    private scheduleTokenRefresh(expiresOn?: Date): void {
        const forcedRefreshIntervalMs = AuthRuntimeConstants.forcedTokenRefreshIntervalMs;
        const effectiveExpiresOn = forcedRefreshIntervalMs > 0
            ? new Date(Date.now() + forcedRefreshIntervalMs + AuthRuntimeConstants.tokenRefreshBufferMs)
            : expiresOn;

        const effectiveDelayMs = effectiveExpiresOn
            ? Math.max(effectiveExpiresOn.getTime() - Date.now() - AuthRuntimeConstants.tokenRefreshBufferMs, 0)
            : undefined;

        securityLog.debug('AuthService: scheduling token refresh', {
            expiresOn: expiresOn?.toISOString() || '',
            forcedRefreshIntervalMs,
            effectiveDelayMs,
        });

        this.authState.scheduleTokenRefresh(effectiveExpiresOn, AuthRuntimeConstants.tokenRefreshBufferMs, () => {
            const pinned = this.accountContext.readPinnedAccountInformation();
            const preferredAccount = this.accountContext.resolvePreferredAccountForRefresh(
                pinned,
                this.accountService.getActiveAccount(),
                this.accountService.getAllAccounts(),
            );

            securityLog.debug('AuthService: scheduled token refresh triggered', {
                pinnedAuthority: pinned?.authority || '',
                pinnedTenant: pinned?.tenant || '',
                pinnedHomeAccountId: pinned?.homeAccountId || '',
                preferredHomeAccountId: preferredAccount?.homeAccountId || preferredAccount?.localAccountId || preferredAccount?.username || '',
                forcedRefreshIntervalMs,
            });

            this.acquireApplicationApiAccessToken(preferredAccount).catch((error) => {
                securityLog.error('AuthService: scheduled token refresh failed', error);
            });
        });
    }

    private restartAuthFlow(): void {
        const protectedResources = this.protectedResourceService.getProtectedResources();
        const scopes = [
            ...protectedResources.applicationApi.scopes.read,
            ...protectedResources.applicationApi.scopes.write,
        ];

        const request = this.authRequestBuilder.createRedirectRequest(scopes);
        const activeAccount = this.accountService.getActiveAccount();

        if (!activeAccount) {
            this.msalService.loginRedirect(request);
            return;
        }

        this.msalService.acquireTokenRedirect(request);
    }

    private async restoreAuthenticatedSession(account: AccountInfo): Promise<void> {
        try {
            const restoredSession = await this.authSession.restoreAuthenticatedSession(account);
            if (!restoredSession) {
                securityLog.warn('AuthService: No valid restored session could be established for cached account');
                return;
            }

            this.publishTokenUpdate(restoredSession.result, restoredSession.authority);

            this.completeAuthenticatedStartup(
                restoredSession.result.account,
                restoredSession.tenant,
                restoredSession.stage,
            );
        } catch (error) {
            securityLog.warn('AuthService: Silent token restoration failed for cached account', error);
        }
    }

    private completeAuthenticatedStartup(
        account: AccountInfo,
        tenant = this.applicationConfig.tenant,
        stage: AuthenticationStage = AuthenticationStage.FreshSignIn,
    ) {
        if (!tenant) {
            securityLog.error('AuthService: Failed to resolve tenant for authenticated startup');
            return;
        }
        
        if (!this.authState.shouldPublishLogin(account, tenant)) {
            securityLog.debug('AuthService: Login already published for this account and tenant, skipping login publication');
            return;
        }

        this.messenger.send(new LoggingInMessage(stage, account));

        this.applicationConfig.tenant = tenant;
        this.messenger.send(new TenantSyncMessage(tenant, account));

        this.authState.markLoginPublished(account, tenant);
        this.messenger.send(new UserLoggedInMessage(account));
    }

    ngOnDestroy(): void {
        this.destroying$.next(undefined);

        this.authSubscription?.unsubscribe();
        this.forgotPasswordSubscription?.unsubscribe();
        this.messenger.unsubscribeByOwner(this);
        this.authState.clearTokenRefreshTimeout();

        this.destroying$.complete();
    }
}
